import { useEffect, useState } from "react";
import {
	AlertTriangle,
	ClipboardCopy,
	ExternalLink,
	Loader2,
	MessagesSquare,
	MonitorPlay,
	PanelRightClose,
	Square,
	X,
} from "lucide-react";
import { RunElapsed } from "@/components/atoms/testing/RunElapsed";
import type { useTestingLayout } from "@/hooks/testing/useTestingLayout";
import { isExecutablePlan } from "@/services/testing";
import { isCredentialError } from "@/utils/testing/planDisplay";
import { RunBlockedNote } from "./RunBlockedNote";
import { ExecutionFeed } from "./ExecutionFeed";
import { healStageLabel } from "./HealProposalCard";
import { LiveBrowserIdle, LiveStage } from "./LiveBrowser";
import { TestingSessionHistory } from "./TestingSessionList";
import { RunMascot } from "./RunResult";

type VM = ReturnType<typeof useTestingLayout>;

export type InspectorTab = "live" | "sessions";

const TABS: Array<{ key: InspectorTab; label: string; icon: typeof MonitorPlay }> = [
	{ key: "live", label: "Live", icon: MonitorPlay },
	{ key: "sessions", label: "History", icon: MessagesSquare },
];

/**
 * The run inspector.
 *
 * Was one long `overflow-y-auto` column stacking live controls, the browser stage, four different
 * banners, the result mascot AND the run-history table — so during a run you had to scroll the live
 * view out of sight to reach history, and the mascot (`flex-1 min-h-[280px]`) fought the history
 * footer (`mt-auto`) for the same space inside a scroll container.
 *
 * Now: two tabs, each owning its own scroll container. Live is what is happening; Runs is what
 * happened. The tab bar also auto-focuses Runs when there is nothing live to watch, so the panel is
 * never showing an idle placeholder while real content sits one click away.
 */
export function TestingInspector({
	vm,
	width,
	onCollapse,
	asDrawer,
}: {
	vm: VM;
	/** Inline column width. Omitted when rendered inside a drawer, which sizes itself in CSS. */
	width?: number;
	onCollapse: () => void;
	asDrawer?: boolean;
}) {
	const {
		selectedPlan,
		runningHere,
		healingHere,
		planningActive,
		liveEvents,
		runResult,
		liveExpanded,
		setLiveExpanded,
		canRun,
		showResultMascot,
		selectedResult,
		hasFreshServerRun,
		runStartedAtMs,
		activeRunId,
		abortRunMutation,
		stopping,
		runError,
		openSfUrl,
		onStop,
		runBlockedReason,
		healStage,
		sessionGroups,
		activeChatScope,
		activeSession,
		selectSessionGroup,
		plansQuery,
	} = vm;

	const busy = runningHere || healingHere;
	const [tab, setTab] = useState<InspectorTab>("live");

	// A starting run pulls attention back to Live; nobody wants to discover their run began by
	// noticing a tab badge. Only fires on the transition into busy, never mid-inspection.
	useEffect(() => {
		if (busy) setTab("live");
	}, [busy]);

	return (
		<aside
			className="tst-panel flex h-full min-h-0 shrink-0 flex-col overflow-hidden border-l"
			style={width != null ? { width } : undefined}
			aria-label="Run details"
		>
			<div className="tst-toolbar flex shrink-0 items-center gap-1 px-1.5">
				{TABS.map(({ key, label, icon: Icon }) => {
					const active = tab === key;
					return (
						<button
							key={key}
							type="button"
							onClick={() => setTab(key)}
							aria-pressed={active}
							className={`tst-interactive inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium ${
								active ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50"
							}`}
						>
							<Icon className="h-3.5 w-3.5" />
							{label}
							{key === "sessions" && sessionGroups.length > 0 && (
								<span className="tabular-nums opacity-60">{sessionGroups.length}</span>
							)}
							{key === "live" && busy && (
								<span
									data-tst-decorative-motion
									className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary"
									aria-hidden
								/>
							)}
						</button>
					);
				})}
				<button
					type="button"
					onClick={onCollapse}
					className="tst-interactive ml-auto flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
					title={asDrawer ? "Close run details (Esc)" : "Collapse run details (⌘J)"}
					aria-label={asDrawer ? "Close run details" : "Collapse run details"}
				>
					{asDrawer ? <X className="h-3.5 w-3.5" /> : <PanelRightClose className="h-3.5 w-3.5" />}
				</button>
			</div>

			{tab === "live" ? (
				<div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
					{!selectedPlan ? (
						<InspectorEmpty />
					) : (
						<>
							<OrgLinkRow openSfUrl={openSfUrl} />

							{selectedPlan.steps && !isExecutablePlan(selectedPlan.steps as never) && (
								<p className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs">
									This plan is in the legacy step-array format and can no longer execute. Re-create it
									via chat to get a runnable Playwright spec.
								</p>
							)}

							<LiveBody
								busy={busy}
								healingHere={healingHere}
								healStage={healStage}
								liveEvents={liveEvents}
								liveExpanded={liveExpanded}
								setLiveExpanded={setLiveExpanded}
								showResultMascot={showResultMascot}
								hasFreshServerRun={hasFreshServerRun}
								planningActive={planningActive}
								canRun={Boolean(canRun)}
								runStartedAtMs={runStartedAtMs}
							/>

							{/* Reconnect banner: the server run is still executing after the stream dropped.
							    Suppressed while an approval is in flight — approving inserts a phantom `running`
							    row server-side, so this would briefly claim a run that never started. */}
							{!busy && liveEvents.length === 0 && !runResult && hasFreshServerRun && (
								<ReconnectBanner
									stopping={stopping}
									runStartedAtMs={runStartedAtMs}
									canStop={Boolean(activeRunId)}
									aborting={abortRunMutation.isPending}
									onStop={onStop}
								/>
							)}

							{/* /execute refused the plan at contract validation. Read-only: the server's own
							    message says what is wrong, and the fix is a re-plan in chat. */}
							{runBlockedReason && <RunBlockedNote reason={runBlockedReason} />}

							{runError && !runBlockedReason && !isCredentialError(runError) && (
								<p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive-strong">
									<AlertTriangle className="mr-1 inline h-3 w-3" />
									{runError}
								</p>
							)}

							{showResultMascot && selectedResult && (
								<div className="flex shrink-0 items-center justify-center py-4">
									<RunMascot status={selectedResult.status} />
								</div>
							)}
							{planningActive && !busy && (
								<div className="flex shrink-0 items-center justify-center py-4">
									<RunMascot status="working" />
								</div>
							)}
						</>
					)}
				</div>
			) : (
				<div className="min-h-0 flex-1 overflow-y-auto">
					<TestingSessionHistory
						sessions={sessionGroups}
						activeScopeKey={activeSession?.scopeKey ?? activeChatScope}
						loading={plansQuery.isLoading || plansQuery.isFetching}
						onSelect={selectSessionGroup}
					/>
				</div>
			)}
		</aside>
	);
}

/**
 * The live stage: the step feed while a run streams, the idle browser card otherwise. Extracted so
 * the tab body reads as a list of concerns rather than a nested ternary.
 */
function LiveBody({
	busy,
	healingHere,
	healStage,
	liveEvents,
	liveExpanded,
	setLiveExpanded,
	showResultMascot,
	hasFreshServerRun,
	planningActive,
	canRun,
	runStartedAtMs,
}: {
	busy: boolean;
	healingHere: boolean;
	healStage: VM["healStage"];
	liveEvents: VM["liveEvents"];
	liveExpanded: boolean;
	setLiveExpanded: (v: boolean) => void;
	showResultMascot: boolean;
	hasFreshServerRun: boolean;
	planningActive: boolean;
	canRun: boolean;
	runStartedAtMs: number | null;
}) {
	const showLive = (busy || liveEvents.length > 0) && !showResultMascot;
	const showIdle =
		!busy && !showResultMascot && liveEvents.length === 0 && !hasFreshServerRun && !planningActive;

	// `liveExpanded` lives in the module-level store, so it outlives both the run and the route. Left
	// sticky, expanding once meant the NEXT run — or simply coming back to Testing — opened straight
	// into a full-screen overlay nobody asked for. Full screen is only ever entered by gesture, so the
	// flag is honoured only while there is a live view to fill it, and cleared as soon as there isn't.
	const expanded = liveExpanded && showLive;
	useEffect(() => {
		if (liveExpanded && !showLive) setLiveExpanded(false);
	}, [liveExpanded, showLive, setLiveExpanded]);

	if (!showLive && !showIdle) return null;

	return (
		<LiveStage expanded={expanded} setExpanded={setLiveExpanded} showExpand={showLive}>
			{showLive &&
				(busy && liveEvents.length === 0 ? (
					<div className="flex flex-col gap-2 p-3">
						<div className="flex items-center gap-2 text-[11px] text-muted-foreground">
							<Loader2 className="h-3 w-3 animate-spin text-primary" />
							<span className="min-w-0 flex-1">
								{healingHere
									? `Preparing fix proposal — ${healStageLabel(healStage)}`
									: "Starting run — launching browser…"}
							</span>
							{runStartedAtMs != null && <RunElapsed startedAt={runStartedAtMs} />}
						</div>
						{[2 / 3, 1 / 2, 3 / 4].map((w) => (
							<div
								key={w}
								data-tst-decorative-motion
								className="h-2 animate-pulse rounded bg-primary/20"
								style={{ width: `${w * 100}%` }}
							/>
						))}
					</div>
				) : (
					<ExecutionFeed
						events={liveEvents}
						isActive={busy}
						expanded={expanded}
						startedAt={runStartedAtMs}
					/>
				))}
			{showIdle && <LiveBrowserIdle canRun={canRun} />}
		</LiveStage>
	);
}

/**
 * The org login link. The raw URL used to be printed as a copyable `<code>` row under the button,
 * which overflowed a narrow panel; it is now the button's own copy affordance.
 */
function OrgLinkRow({ openSfUrl }: { openSfUrl: string | null }) {
	const [copied, setCopied] = useState(false);
	useEffect(() => {
		if (!copied) return;
		const t = setTimeout(() => setCopied(false), 1500);
		return () => clearTimeout(t);
	}, [copied]);

	return (
		<div className="flex shrink-0 items-center gap-1.5">
			<a
				href={openSfUrl || undefined}
				target="_blank"
				rel="noopener noreferrer"
				aria-disabled={!openSfUrl}
				onClick={(e) => {
					if (!openSfUrl) e.preventDefault();
				}}
				className={`tst-interactive inline-flex h-8 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md border border-input bg-card px-2 text-[11px] font-medium ${
					openSfUrl
						? "text-foreground hover:bg-muted/40"
						: "cursor-not-allowed text-muted-foreground/50"
				}`}
				title={openSfUrl ? `Open ${openSfUrl}` : "Loading login URL…"}
			>
				<ExternalLink className="h-3.5 w-3.5 shrink-0" />
				<span className="truncate">Open in Salesforce</span>
			</a>
			{openSfUrl && (
				<button
					type="button"
					onClick={() => {
						void navigator.clipboard
							.writeText(openSfUrl)
							.then(() => setCopied(true))
							.catch(() => undefined);
					}}
					className="tst-interactive inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-input bg-card text-muted-foreground hover:bg-muted/40 hover:text-foreground"
					title={copied ? "Copied" : "Copy the login URL"}
					aria-label="Copy the login URL"
				>
					{copied ? (
						<span className="text-[11px] font-semibold text-success-strong">✓</span>
					) : (
						<ClipboardCopy className="h-3.5 w-3.5" />
					)}
				</button>
			)}
		</div>
	);
}

function ReconnectBanner({
	stopping,
	runStartedAtMs,
	canStop,
	aborting,
	onStop,
}: {
	stopping: boolean;
	runStartedAtMs: number | null;
	canStop: boolean;
	aborting: boolean;
	onStop: () => void;
}) {
	return (
		<div
			className={`flex flex-wrap items-center gap-2 rounded-md border p-2 text-xs text-foreground ${
				stopping ? "border-destructive/40 bg-destructive/10" : "border-primary/40 bg-primary/10"
			}`}
		>
			<Loader2
				className={`h-3 w-3 shrink-0 animate-spin ${stopping ? "text-destructive-strong" : "text-primary"}`}
			/>
			<span className="min-w-0 flex-1">
				{stopping
					? "Stopping this run — sending the abort to the server and winding it down…"
					: "Reconnected — this run is still executing on the server. Its steps update live and the result appears here when it finishes."}
			</span>
			{runStartedAtMs != null && !stopping && <RunElapsed startedAt={runStartedAtMs} reassure />}
			{canStop && (
				<button
					type="button"
					onClick={onStop}
					disabled={aborting || stopping}
					className="tst-interactive inline-flex shrink-0 items-center gap-1 rounded border border-destructive/50 bg-destructive/10 px-2 py-0.5 font-medium text-destructive-strong hover:bg-destructive/20 disabled:opacity-50"
					title="Stop the run executing on the server"
				>
					<Square className="h-3 w-3" />
					{aborting || stopping ? "Stopping…" : "Stop run"}
				</button>
			)}
		</div>
	);
}

/**
 * One quiet line when nothing is selected. The old version was a third competing empty state that
 * also carried the Suites and Coverage buttons — which is why those two features vanished from the
 * product the moment a test was selected. They now live permanently in the session list footer.
 */
function InspectorEmpty() {
	return (
		<div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center">
			<MonitorPlay className="h-6 w-6 text-muted-foreground/40" />
			<p className="text-[11px] leading-relaxed text-muted-foreground">
				The live browser appears here once a test is running.
			</p>
		</div>
	);
}
